import { useState, useEffect, useCallback, useRef } from 'react';
import { checkoutAPI, CheckoutInvalidResponseError } from './checkout-api';
import {
  checkoutClientContext,
  checkoutElapsedSinceMS,
  checkoutNavigationMetrics,
  checkoutPageElapsedMS,
  checkoutSourceContext,
} from './checkout-telemetry';
import type {
  CheckoutInteractionInput,
  CheckoutSession,
  CheckoutState,
  PaytmJSCheckoutLaunch,
  UPIApp,
} from '../types/checkout';

const PAYTM_RETRY_MESSAGE = 'Payment window did not open. Tap Pay to try again.';
const PAYTM_CLOSED_MESSAGE = 'Payment window was closed. Tap Pay to try again.';

interface UseCheckoutStateResult {
  state: CheckoutState;
  session: CheckoutSession | null;
  error: string | null;
  isExpired: boolean;
  upiIntent: string | null;
  handlePay: (targetUrl?: string, upiApp?: UPIApp) => Promise<void>;
  handleUPIChooserReady: () => void;
  handleSimulatePayment: (status: 'SUCCESS' | 'FAILED') => Promise<void>;
  handleReturnToMerchant: () => void;
  handleCancel: () => void;
}

function getValidatedInitialUPILaunch(session: CheckoutSession) {
  const launch = session.initial_launch;
  const intent = launch?.upi_intent?.trim();
  if (
    !launch ||
    launch.platform_order_id !== session.platform_order_id ||
    launch.payment_status !== 'PENDING' ||
    typeof intent !== 'string' ||
    !/^upi:\/\/pay(?:\?|$)/i.test(intent)
  ) {
    return null;
  }
  return { ...launch, upi_intent: intent };
}

/**
 * validateFormPostLaunch returns the trimmed action + the form_fields
 * map when both meet the strict FORM_POST shape contract; returns null
 * otherwise so the caller can fail closed without leaking why.
 *
 * Strict shape (issue #921 review):
 *   - action: trimmed non-empty string parseable as an absolute https:
 *     URL. http: is refused because payment form-post gateways are only
 *     served over TLS — http would either fail mixed-content blocking
 *     in the browser or downgrade a payment session to plaintext.
 *     Relative URLs are also refused (not what the backend produces;
 *     would let an attacker-shaped payload navigate to a same-origin
 *     resource).
 *   - fields: a non-null PLAIN object (not an array, not a Date, not a
 *     class instance), with at least one entry, every key a non-empty
 *     trimmed string, every value a string.
 *
 * Returns the same fields object reference on success — submitFormPost
 * iterates Object.entries which only enumerates the object's own
 * enumerable properties, and the plain-object prototype guard above
 * already rejects non-Object instances, so we don't need to defensively
 * rebuild the map here.
 */
function validateFormPostLaunch(
  rawAction: unknown,
  rawFields: unknown,
): { action: string; fields: Record<string, string> } | null {
  if (typeof rawAction !== 'string') {
    return null;
  }
  const action = rawAction.trim();
  if (action.length === 0) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(action);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') {
    return null;
  }

  if (rawFields === null || typeof rawFields !== 'object' || Array.isArray(rawFields)) {
    return null;
  }
  // Reject class instances (Map, Set, Date, FormData, …). The backend
  // sends a JSON object, which deserializes to a literal Object whose
  // prototype is Object.prototype.
  const proto = Object.getPrototypeOf(rawFields);
  if (proto !== Object.prototype && proto !== null) {
    return null;
  }

  const entries = Object.entries(rawFields as Record<string, unknown>);
  if (entries.length === 0) {
    return null;
  }
  for (const [key, value] of entries) {
    if (typeof key !== 'string' || key.trim().length === 0) {
      return null;
    }
    if (typeof value !== 'string') {
      return null;
    }
  }
  return { action, fields: rawFields as Record<string, string> };
}

/**
 * Custom hook for managing checkout state
 * Handles fetching session data, processing payments, and managing UI states
 */
export function useCheckoutState(token: string): UseCheckoutStateResult {
  const [state, setState] = useState<CheckoutState>('loading');
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [upiIntent, setUpiIntent] = useState<string | null>(null);
  const [telemetrySessionID] = useState(() => checkoutAPI.createInteractionSessionID());

  // Submit lock to prevent multiple simultaneous pay button clicks
  const isSubmittingRef = useRef(false);
  const cryptoRefreshInFlightRef = useRef<AbortController | null>(null);
  const pageOpenedRecordedRef = useRef(false);
  const pageLoadedRecordedRef = useRef(false);
  const sessionLoadedRecordedRef = useRef(false);
  const chooserReadyRecordedRef = useRef(false);
  const loadFailureStagesRef = useRef(new Set<string>());
  const lastLaunchedUPIAppRef = useRef<UPIApp | null>(null);
  const leftForUPIAppRef = useRef(false);

  const recordInteraction = useCallback((
    input: Omit<CheckoutInteractionInput, 'client_session_id'>,
  ) => {
    try {
      checkoutAPI.recordCheckoutInteraction(token, {
        client_session_id: telemetrySessionID,
        ...input,
      });
    } catch {
      // Telemetry is observation-only and can never affect checkout behavior.
    }
  }, [telemetrySessionID, token]);

  const recordLoadFailure = useCallback((
    stage: NonNullable<CheckoutInteractionInput['failure_stage']>,
    code: NonNullable<CheckoutInteractionInput['failure_code']>,
    operationStartedAtMS?: number,
  ) => {
    if (loadFailureStagesRef.current.has(stage)) return;
    loadFailureStagesRef.current.add(stage);
    recordInteraction({
      event_type: 'CHECKOUT_LOAD_FAILED',
      failure_stage: stage,
      failure_code: code,
      page_elapsed_ms: checkoutPageElapsedMS(),
      operation_duration_ms: checkoutElapsedSinceMS(operationStartedAtMS),
    });
  }, [recordInteraction]);

  // Track if payment was already initiated (persisted in sessionStorage for re-entry detection)
  const getSessionStorageKey = useCallback((t: string) => `checkout_payment_initiated_${t}`, []);
  const wasPaymentInitiated = useCallback(() => {
    try {
      return sessionStorage.getItem(getSessionStorageKey(token)) === 'true';
    } catch {
      return false;
    }
  }, [token, getSessionStorageKey]);

  const markPaymentInitiated = useCallback(() => {
    try {
      sessionStorage.setItem(getSessionStorageKey(token), 'true');
    } catch {
      // Ignore sessionStorage errors
    }
  }, [token, getSessionStorageKey]);

  const clearPaymentInitiated = useCallback(() => {
    try {
      sessionStorage.removeItem(getSessionStorageKey(token));
    } catch {
      // Ignore sessionStorage errors
    }
  }, [token, getSessionStorageKey]);

  useEffect(() => {
    const recordPageLoaded = () => {
      if (pageLoadedRecordedRef.current) return;
      pageLoadedRecordedRef.current = true;
      const pageElapsedMS = checkoutPageElapsedMS();
      const navigationMetrics = checkoutNavigationMetrics();
      recordInteraction({
        event_type: 'CHECKOUT_PAGE_LOADED',
        page_elapsed_ms: pageElapsedMS,
        ...navigationMetrics,
        window_load_ms: navigationMetrics.window_load_ms ?? pageElapsedMS,
      });
    };

    if (document.readyState === 'complete') {
      recordPageLoaded();
      return;
    }
    window.addEventListener('load', recordPageLoaded, { once: true });
    return () => window.removeEventListener('load', recordPageLoaded);
  }, [recordInteraction]);

  const invokePaytmJSCheckout = useCallback(async (paytmLaunch: PaytmJSCheckoutLaunch | undefined) => {
    let appClosed = false;

    await checkoutAPI.invokePaytmJSCheckout(paytmLaunch, (eventName) => {
      if (eventName === 'APP_CLOSED') {
        appClosed = true;
        clearPaymentInitiated();
        isSubmittingRef.current = false;
        setError(PAYTM_CLOSED_MESSAGE);
        setState('payment');
      }
    });

    if (appClosed) {
      return;
    }

    markPaymentInitiated();
    setError(null);
    setState('waiting');
  }, [clearPaymentInitiated, markPaymentInitiated]);

  // Fetch checkout session on mount
  useEffect(() => {
    async function fetchSession() {
      const sessionRequestStartedAtMS = checkoutPageElapsedMS();
      try {
        const sessionData = await checkoutAPI.getSession(token);
        if (sessionData.payment_method_code === 'UPI' && !sessionLoadedRecordedRef.current) {
          sessionLoadedRecordedRef.current = true;
          recordInteraction({
            event_type: 'CHECKOUT_SESSION_LOADED',
            page_elapsed_ms: checkoutPageElapsedMS(),
            operation_duration_ms: checkoutElapsedSinceMS(sessionRequestStartedAtMS),
          });
        }

        // A pending crypto transfer remains displayable after its channel
        // payment window reaches zero so the frontend can continue polling for
        // a late callback/query result. Its own countdown handles the warning.
        const isPendingCryptoTransfer =
          sessionData.payment_status === 'PENDING' && Boolean(sessionData.crypto_transfer);

        // Check if token is expired for non-crypto checkout modes.
        const expiresAt = new Date(sessionData.expires_at);
        if (expiresAt < new Date() && !isPendingCryptoTransfer) {
          setIsExpired(true);
          setSession(sessionData); // Set session for return_url access
          setState('expired');
          return;
        }

        setSession(sessionData);

        // Determine initial state based on payment status
        if (sessionData.payment_status === 'SUCCESS') {
          setState('success');
        } else if (sessionData.payment_status === 'FAILED') {
          setState('failure');
        } else if (sessionData.payment_status === 'CANCELLED') {
          setState('cancel');
        } else if (sessionData.crypto_transfer) {
          setState('crypto_transfer');
        } else if (sessionData.uat_manual_simulator?.enabled) {
          setState('uat_simulator');
        } else {
          const isHostedUPI = sessionData.payment_method_code === 'UPI';
          const isPaytmCheckout = sessionData.channel_code?.trim().toUpperCase() === 'PAYTM';

          if (!isHostedUPI && wasPaymentInitiated()) {
            setState('waiting');
            return;
          }

          // Hosted UPI flows: always show the chooser for pending orders,
          // even if a previous payment attempt was initiated. The chooser
          // must remain available so the user can retry another app (issue #579).
          if (isHostedUPI || isPaytmCheckout) {
            const launchRequestStartedAtMS = checkoutPageElapsedMS();
            try {
              // Normal UPI sessions carry a validated launch snapshot so the
              // chooser can render after one API request. Any absent or
              // malformed snapshot falls back to the established /launch
              // endpoint without changing payment behavior.
              const launchData = getValidatedInitialUPILaunch(sessionData) ??
                await checkoutAPI.launchCheckout(token);
              if (launchData.upi_intent) {
                setUpiIntent(launchData.upi_intent);
                setState('upi_chooser');
                return;
              }

              const hasAlternativeLaunch = Boolean(launchData.redirect_url) ||
                launchData.launch_method === 'FORM_POST' ||
                launchData.launch_method === 'PAYTM_JS_CHECKOUT';
              if (isHostedUPI && !hasAlternativeLaunch) {
                recordLoadFailure('launch', 'NO_UPI_INTENT', launchRequestStartedAtMS);
              }

              if (launchData.launch_method === 'PAYTM_JS_CHECKOUT') {
                const paymentInitiated = wasPaymentInitiated();
                if (paymentInitiated) {
                  setState('waiting');
                  return;
                }

                isSubmittingRef.current = true;
                setError(null);
                setState('submitting');
                const renderStartedAtMS = checkoutPageElapsedMS();
                try {
                  await invokePaytmJSCheckout(launchData.paytm_js_checkout);
                } catch {
                  if (isHostedUPI) {
                    recordLoadFailure('render', 'RENDER_FAILED', renderStartedAtMS);
                  }
                  clearPaymentInitiated();
                  setError(PAYTM_RETRY_MESSAGE);
                  setState('payment');
                } finally {
                  isSubmittingRef.current = false;
                }
                return;
              }
            } catch (launchErr) {
              if (isHostedUPI) {
                recordLoadFailure(
                  'launch',
                  launchErr instanceof CheckoutInvalidResponseError ? 'INVALID_RESPONSE' : 'REQUEST_FAILED',
                  launchRequestStartedAtMS,
                );
                const launchMessage = launchErr instanceof Error ? launchErr.message : 'Failed to load payment options';
                setError(launchMessage);
                setState('error');
              } else {
                setState('payment');
              }
              return;
            }
          }

          // Non-UPI flows: if payment was previously initiated, show waiting
          const paymentInitiated = wasPaymentInitiated();
          if (paymentInitiated) {
            setState('waiting');
            return;
          }

          setState('payment');
        }
      } catch (err) {
        recordLoadFailure(
          'session',
          err instanceof CheckoutInvalidResponseError ? 'INVALID_RESPONSE' : 'REQUEST_FAILED',
          sessionRequestStartedAtMS,
        );
        const errorMessage = err instanceof Error ? err.message : 'Failed to load checkout session';

        if (errorMessage === 'INVALID_TOKEN') {
          setState(isExpired ? 'expired' : 'invalid');
        } else {
          setState('error');
          setError(errorMessage);
        }
      }
    }

    void fetchSession();
    if (!pageOpenedRecordedRef.current) {
      pageOpenedRecordedRef.current = true;
      recordInteraction({
        event_type: 'CHECKOUT_PAGE_OPENED',
        source_context: checkoutSourceContext(),
        page_elapsed_ms: checkoutPageElapsedMS(),
        ...checkoutClientContext(),
      });
    }
  }, [token, wasPaymentInitiated, clearPaymentInitiated, invokePaytmJSCheckout, recordInteraction, recordLoadFailure]);

  const handleUPIChooserReady = useCallback(() => {
    if (chooserReadyRecordedRef.current) return;
    chooserReadyRecordedRef.current = true;
    recordInteraction({
      event_type: 'CHECKOUT_READY',
      page_elapsed_ms: checkoutPageElapsedMS(),
      ...checkoutNavigationMetrics(),
    });
  }, [recordInteraction]);

  // Handle pay button click
  const handlePay = useCallback(async (targetUrl?: string, upiApp?: UPIApp) => {
    if (!session) return;

    // Submit lock: prevent multiple simultaneous clicks
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (targetUrl) {
      if (upiApp) {
        lastLaunchedUPIAppRef.current = upiApp;
        leftForUPIAppRef.current = false;
        try {
          checkoutAPI.recordUPIAppLaunch(token, upiApp, telemetrySessionID, checkoutPageElapsedMS());
        } catch {
          // A telemetry implementation failure must never prevent navigation.
        }
      }
      markPaymentInitiated();
      // Keep the chooser visible for UPI deep-link launch.
      // If jump-out fails or the user returns before platform status changes,
      // the page remains usable and the refresh effect can still reconcile state.
      try {
        checkoutAPI.redirectToPayment(targetUrl);
      } catch {
        // Deep link failed — stay on chooser
      } finally {
        isSubmittingRef.current = false;
      }
      return;
    }

    // NOTE: markPaymentInitiated is intentionally NOT called here.
    // It must only fire AFTER a valid launch path has been selected
    // (UPI chooser, valid FORM_POST, or legacy redirect), so that a
    // failed launchCheckout call or a malformed FORM_POST payload does
    // not leave checkout_payment_initiated=true in sessionStorage —
    // which would otherwise force the next page load into the 'waiting'
    // state with no way to retry. (issue #921 review)
    setError(null);
    setState('submitting');

    try {
      const launchData = await checkoutAPI.launchCheckout(token);

      // If UPI intent is present, show app chooser instead of direct redirect
      if (launchData.upi_intent) {
        markPaymentInitiated();
        setUpiIntent(launchData.upi_intent);
        setState('upi_chooser');
        return;
      }

      // FORM_POST launches: submit a hidden form POST instead of a
      // window.location redirect. Some gateways require per-transaction
      // session params in the request body, and a GET on the redirect URL
      // would drop them.
      //
      // Fail closed on malformed payloads: when launch_method declares
      // FORM_POST but form_action / form_fields don't meet the strict
      // shape contract, surface a generic retry error instead of
      // silently falling back to redirectToPayment(redirect_url). The safer
      // behaviour is to refuse and let the user retry. (issue #921 review)
      if (launchData.launch_method === 'FORM_POST') {
        const validated = validateFormPostLaunch(launchData.form_action, launchData.form_fields);
        if (!validated) {
          throw new Error('Payment session is not ready — please retry in a moment');
        }
        markPaymentInitiated();
        checkoutAPI.submitFormPost(validated.action, validated.fields);
        return;
      }

      if (launchData.launch_method === 'PAYTM_JS_CHECKOUT') {
        const renderStartedAtMS = checkoutPageElapsedMS();
        try {
          await invokePaytmJSCheckout(launchData.paytm_js_checkout);
        } catch {
          if (session.payment_method_code === 'UPI') {
            recordLoadFailure('render', 'RENDER_FAILED', renderStartedAtMS);
          }
          clearPaymentInitiated();
          setError(PAYTM_RETRY_MESSAGE);
          setState('payment');
        }
        return;
      }

      // Redirect directly to payment channel (legacy / REDIRECT launch_method)
      markPaymentInitiated();
      checkoutAPI.redirectToPayment(launchData.redirect_url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process payment';
      setError(errorMessage);
      setState('error');
    } finally {
      // Reset submit lock
      isSubmittingRef.current = false;
    }
  }, [session, token, telemetrySessionID, markPaymentInitiated, clearPaymentInitiated, invokePaytmJSCheckout, recordLoadFailure]);

  const handleSimulatePayment = useCallback(async (status: 'SUCCESS' | 'FAILED') => {
    if (!session?.uat_manual_simulator?.enabled) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setState('submitting');
    try {
      const freshSession = await checkoutAPI.simulateUATManualPayment(token, status);
      setSession(freshSession);

      if (freshSession.payment_status === 'SUCCESS') {
        setState('success');
      } else if (freshSession.payment_status === 'FAILED') {
        setState('failure');
      } else {
        setState('uat_simulator');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to simulate payment';
      setError(errorMessage);
      setState('error');
    } finally {
      isSubmittingRef.current = false;
    }
  }, [session, token]);

  // On-chain transfer status refresh. The browser only talks to our checkout
  // API; it never queries a channel directly. Hidden tabs skip their interval
  // refresh, then reconcile immediately when visible again.
  const cryptoSessionExpiresAt = session?.expires_at;
  useEffect(() => {
    if (state !== 'crypto_transfer') return;

    let active = true;
    let effectController: AbortController | null = null;
    let timer: number | undefined;

    const refreshCryptoStatus = async () => {
      if (!active || document.visibilityState !== 'visible' || cryptoRefreshInFlightRef.current) {
        return;
      }

      const controller = new AbortController();
      effectController = controller;
      cryptoRefreshInFlightRef.current = controller;
      let timeoutID: number | undefined;
      try {
        const timeout = new Promise<never>((_resolve, reject) => {
          timeoutID = window.setTimeout(() => {
            controller.abort();
            reject(new Error('Crypto status refresh timed out'));
          }, 4000);
        });
        const freshSession = await Promise.race([
          checkoutAPI.getSession(token, { signal: controller.signal, timeoutMs: 4000 }),
          timeout,
        ]);
        if (!active) return;

        setSession(freshSession);
        if (freshSession.payment_status === 'SUCCESS') {
          setState('success');
        } else if (freshSession.payment_status === 'FAILED') {
          setState('failure');
        } else if (freshSession.payment_status === 'CANCELLED') {
          setState('cancel');
        }
      } catch (err) {
        if (!active) return;

        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage === 'INVALID_TOKEN') {
          // A 401 is terminal for this checkout token, not a transient channel
          // refresh failure. Stop both interval and visibility-driven polling.
          active = false;
          if (timer !== undefined) {
            window.clearInterval(timer);
          }

          const expiresAtMs = cryptoSessionExpiresAt
            ? Date.parse(cryptoSessionExpiresAt)
            : Number.NaN;
          const tokenExpired = Number.isFinite(expiresAtMs) && Date.now() > expiresAtMs;
          setIsExpired(tokenExpired);
          setState(tokenExpired ? 'expired' : 'invalid');
          return;
        }

        // A transient refresh failure must not hide the transfer instructions.
      } finally {
        if (timeoutID !== undefined) {
          window.clearTimeout(timeoutID);
        }
        if (cryptoRefreshInFlightRef.current === controller) {
          cryptoRefreshInFlightRef.current = null;
        }
        if (effectController === controller) {
          effectController = null;
        }
      }
    };

    timer = window.setInterval(() => void refreshCryptoStatus(), 5000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshCryptoStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      active = false;
      if (effectController) {
        effectController.abort();
        if (cryptoRefreshInFlightRef.current === effectController) {
          cryptoRefreshInFlightRef.current = null;
        }
        effectController = null;
      }
      if (timer !== undefined) {
        window.clearInterval(timer);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [state, token, cryptoSessionExpiresAt]);

  // Browser-return status refresh (issue #579):
  // When the customer returns from a UPI app, re-check order status.
  // If SUCCESS, switch immediately. Debounced to avoid excessive API calls.
  //
  // Residual limitation: if the external payment succeeded but the platform
  // has not yet received callback/query confirmation, the frontend cannot
  // prove success in time. This is a callback/query latency limitation,
  // not something the checkout frontend alone can fully solve.
  const lastRefreshRef = useRef<number>(0);
  const REFRESH_DEBOUNCE_MS = 3000;

  useEffect(() => {
    if (state !== 'upi_chooser' && state !== 'waiting') return;

    const refreshStatus = async () => {
      const now = Date.now();
      if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) return;
      lastRefreshRef.current = now;

      try {
        const freshSession = await checkoutAPI.getSession(token);
        setSession(freshSession);

        if (freshSession.payment_status === 'SUCCESS') {
          setState('success');
        } else if (freshSession.payment_status === 'FAILED') {
          setState('failure');
        }
      } catch {
        // Silently ignore refresh errors
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && lastLaunchedUPIAppRef.current) {
        leftForUPIAppRef.current = true;
      }
      if (document.visibilityState === 'visible') {
        if (leftForUPIAppRef.current && lastLaunchedUPIAppRef.current) {
          const returnedFromApp = lastLaunchedUPIAppRef.current;
          recordInteraction({
            event_type: 'CHECKOUT_RESUMED',
            payment_app: returnedFromApp,
            page_elapsed_ms: checkoutPageElapsedMS(),
          });
          leftForUPIAppRef.current = false;
          lastLaunchedUPIAppRef.current = null;
        }
        void refreshStatus();
      }
    };
    const onFocus = () => void refreshStatus();
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void refreshStatus();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [state, token, recordInteraction]);

  // Handle return to merchant
  const handleReturnToMerchant = useCallback(() => {
    if (session?.return_url) {
      let status: 'success' | 'failure' | 'cancel' | undefined;

      if (state === 'success') status = 'success';
      else if (state === 'failure') status = 'failure';
      else if (state === 'cancel') status = 'cancel';
      // For waiting, expired, invalid, and error states, return without status
      // The merchant can handle these defensive returns appropriately

      checkoutAPI.returnToMerchant(session.return_url, status);
    }
  }, [session, state]);

  // Handle cancel - return to merchant with cancel status
  const handleCancel = useCallback(() => {
    setState('cancel');
    if (session?.return_url) {
      checkoutAPI.returnToMerchant(session.return_url, 'cancel');
    }
  }, [session]);

  return {
    state,
    session,
    error,
    isExpired,
    upiIntent,
    handlePay,
    handleUPIChooserReady,
    handleSimulatePayment,
    handleReturnToMerchant,
    handleCancel,
  };
}
